/**
 * Notification schemas
 */
import { z } from "zod";
import { NestedPaginationParamsSchema, SortParamsSchema, StrapiEntitySchema, StrapiIdSchema } from "./common.schemas";
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Notification type schema
 */
export const NotificationTypeSchema = z
    .enum([
    "trade_opened",
    "trade_closed",
    "deposit",
    "withdrawal",
    "payment_success",
    "payment_failed",
    "subscription_activated",
    "subscription_canceled",
    "bot_connected",
    "bot_disconnected",
    "bot_error",
    "system",
    "marketing",
])
    .or(z.string());
/**
 * Platform schema
 */
export const PlatformSchema = z.enum(["ios", "android", "web"]);
/**
 * Email status schema
 */
export const EmailStatusSchema = z.enum(["sent", "failed", "bounced", "delivered", "opened", "clicked"]);
/**
 * Email system health status schema
 */
export const EmailSystemHealthSchema = z.enum(["healthy", "degraded", "down"]);
/**
 * Notification query params schema
 */
export const NotificationQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        type: z.object({ $eq: NotificationTypeSchema.optional() }).strict().optional(),
        isRead: z.object({ $eq: z.boolean().optional() }).strict().optional(),
        sentDate: z
            .object({
            $gte: z.string().optional(),
            $lte: z.string().optional(),
        })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Register notification token input schema
 */
export const RegisterNotificationTokenInputSchema = z
    .object({
    token: z.string().min(1, "Notification token is required"),
    platform: PlatformSchema,
    deviceId: z.string().optional(),
})
    .strict();
/**
 * Mark events read input schema
 */
export const MarkEventsReadInputSchema = z
    .object({
    eventIds: z.array(StrapiIdSchema).min(1, "At least one event ID is required"),
})
    .strict();
/**
 * Event query params schema
 */
export const EventQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    // Legacy/custom params used by dashboard
    configUid: z.string().optional(),
    limit: z.number().optional(),
    filters: z
        .object({
        type: z.object({ $eq: NotificationTypeSchema.optional() }).strict().optional(),
        isRead: z.object({ $eq: z.boolean().optional() }).strict().optional(),
        createdAt: z
            .object({
            $gte: z.string().optional(),
            $lte: z.string().optional(),
        })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Email template query params schema
 */
export const EmailTemplateQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        trigger: z.object({ $eq: z.string().optional() }).strict().optional(),
        isActive: z.object({ $eq: z.boolean().optional() }).strict().optional(),
        language: z.object({ $eq: z.string().optional() }).strict().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Email template update input schema
 */
export const EmailTemplateUpdateInputSchema = z
    .object({
    subject: z.string().optional(),
    body: z.string().optional(),
    isActive: z.boolean().optional(),
})
    .strict();
/**
 * Email log query params schema
 * Note: email-log controller uses direct params, not Strapi-style filters
 */
export const EmailLogQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    // Direct query params (not Strapi-style filters)
    trigger: z.string().optional(),
    status: z.string().optional(),
    recipient: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    isTest: z.union([z.boolean(), z.string()]).optional(),
})
    .strict();
/**
 * Send test email input schema
 */
export const SendTestEmailInputSchema = z
    .object({
    trigger: z.string().min(1, "Trigger is required"),
    userId: StrapiIdSchema.optional(),
    customEmail: z.string().email().optional(),
})
    .strict();
/**
 * Send all test emails input schema
 */
export const SendAllTestEmailsInputSchema = z
    .object({
    userId: StrapiIdSchema.optional(),
    customEmail: z.string().email().optional(),
    triggers: z.array(z.string()).optional(),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Notification content schema (title/body generated by extend-notifications-populate middleware)
 */
export const NotificationContentSchema = z.object({
    title: z.string(),
    body: z.string(),
});
/**
 * Notification schema
 *
 * The Strapi messaging plugin stores raw config/i18nValues and generates
 * title/body at query time via the extend-notifications-populate middleware,
 * nesting them under a `content` object.
 */
export const NotificationSchema = StrapiEntitySchema.extend({
    content: NotificationContentSchema.nullish(),
    type: NotificationTypeSchema,
    sentDate: z.string(),
    read: z.boolean().nullish(),
    config: z.record(z.unknown()).nullish(),
    i18nValues: z.record(z.unknown()).nullish(),
    successCount: z.number().nullish(),
});
/**
 * Notification token schema
 */
export const NotificationTokenSchema = StrapiEntitySchema.extend({
    token: z.string(),
    platform: PlatformSchema,
    deviceId: z.string().nullish(),
    isActive: z.boolean(),
    lastUsed: z.string().nullish(),
    // Relations
    user: z.object({ id: StrapiIdSchema }).nullish(),
});
/**
 * Bot-config event type schema
 *
 * Mirrors `@investhinks/types` `EventTypeSchema`, kept local to avoid a cross-package
 * dependency. `.or(z.string())` keeps forward compatibility with new event types.
 */
export const BotEventTypeSchema = z
    .enum([
    "STOP_LOSS",
    "STOP_INSUFFICIENT_FUNDS",
    "STOP_BALANCE_TOO_HIGH",
    "STOP_LEVERAGE_TOO_LOW",
    "STOP_LEVERAGE_TOO_HIGH",
    "STOP_INVALID_CREDENTIALS",
    "STOP_CREDIT_BONUS_RESTRICTED",
    "LOW_MARGIN_WARNING",
    "DRAWDOWN_WARNING",
    "STRATEGY_CHANGED",
    "CONFIG_CHANGED",
])
    .or(z.string());
/**
 * Event schema
 *
 * Matches the `/bot-config-events/me` payload (controller bot-config-event shape),
 * NOT the messaging-notification shape. See the api service that builds it:
 * apps/api/src/api/bot-config-event/services/custom/getEventsForUser.ts
 */
export const EventSchema = z.object({
    id: StrapiIdSchema,
    timestamp: z.number(),
    type: BotEventTypeSchema,
    configUid: z.string().nullish(),
    credentialsUid: z.string().nullish(),
    details: z.record(z.unknown()).default({}),
    configName: z.string().nullish(),
    accountName: z.string().nullish(),
    isRead: z.boolean(),
    exchangeAccountId: z.number().nullish(),
});
/**
 * Email template schema
 */
export const EmailTemplateSchema = StrapiEntitySchema.extend({
    trigger: z.string(),
    subject: z.string().nullish(),
    body: z.string().nullish(),
    isActive: z.boolean().nullish(),
    language: z.string().nullish(),
    variables: z.array(z.string()).nullish(),
    lastModified: z.string().nullish(),
    lastSent: z.string().nullish(),
    // Additional fields used in structured templates
    title: z.string().nullish(),
    titleColor: z.string().nullish(),
    greeting: z.string().nullish(),
    bodyParagraphs: z.array(z.string()).nullish(),
    buttonText: z.string().nullish(),
    buttonUrl: z.string().nullish(),
    closingText: z.string().nullish(),
});
/**
 * Grouped email templates response schema
 * API returns templates grouped by trigger: { data: { trigger1: [...], trigger2: [...] } }
 */
export const GroupedEmailTemplatesSchema = z.object({
    data: z.record(z.string(), z.array(EmailTemplateSchema)),
});
/**
 * Email trigger metadata schema (returned by getAllTriggers)
 */
export const EmailTriggerMetadataSchema = z.object({
    trigger: z.string(),
    label: z.object({
        en: z.string(),
        es: z.string(),
    }),
    description: z.string(),
    variables: z.array(z.string()),
});
/**
 * Email template preview schema (returned by preview endpoint for editing)
 */
export const EmailTemplatePreviewSchema = z.object({
    subject: z.string(),
    html: z.string(),
});
/**
 * Email test preview schema (returned by previewTestEmail for testing page)
 */
export const EmailTestPreviewSchema = z.object({
    trigger: z.string(),
    triggerLabel: z.string(),
    description: z.string(),
    recipient: z.string(),
    language: z.string(),
    variables: z.record(z.string()),
    previewSubject: z.string(),
    previewHtml: z.string(),
    templateFound: z.boolean(),
    templateActive: z.boolean(),
});
/**
 * Search user result schema (returned by searchUsers)
 */
export const EmailTestSearchUserSchema = z.object({
    id: StrapiIdSchema,
    email: z.string(),
    displayName: z.string(),
    language: z.string(),
});
/**
 * Email template variable schema
 */
export const EmailTemplateVariableSchema = z.object({
    name: z.string(),
    description: z.string(),
    example: z.string().nullish(),
});
/**
 * Email template variables schema
 * Note: API returns variables as simple string array, not array of objects
 */
export const EmailTemplateVariablesSchema = z.object({
    trigger: z.string(),
    variables: z.array(z.string()),
});
/**
 * Email log schema
 */
export const EmailLogSchema = StrapiEntitySchema.extend({
    trigger: z.string().nullish(),
    recipient: z.string().nullish(),
    subject: z.string().nullish(),
    status: EmailStatusSchema.or(z.string()).nullish(),
    sentAt: z.string().nullish(),
    error: z.string().nullish(),
    errorMessage: z.string().nullish(), // Alternative field name for error
    metadata: z.record(z.unknown()).nullish(),
    // Additional fields from API
    isTest: z.boolean().nullish(),
    bodyHtml: z.string().nullish(),
    variables: z.record(z.unknown()).nullish(),
    // Relations
    user: z.object({ id: StrapiIdSchema }).nullish(),
});
/**
 * Email stats schema - matches Strapi email-log service getStats
 */
export const EmailStatsSchema = z.object({
    total: z.number(),
    // byStatus breakdown
    byStatus: z.object({
        sent: z.number(),
        failed: z.number(),
        pending: z.number(),
    }),
    // Count per trigger
    byTrigger: z.record(z.number()),
});
/**
 * Email system status schema
 */
export const EmailSystemStatusSchema = z.object({
    isConfigured: z.boolean().nullish(),
    provider: z.string().nullish(),
    lastCheck: z.string().nullish(),
    status: EmailSystemHealthSchema.or(z.string()).nullish(),
    queueSize: z.number().nullish(),
    // Testing endpoint specific fields
    notificationsEnabled: z.boolean().nullish(),
    message: z.string().nullish(),
    envVariable: z.string().nullish(),
    currentValue: z.string().nullish(),
});
